import React from "react";
import { SECTION_LABELS } from "../../lib/sections.js";

/**
 * "Drawn from" chips for one scene (contract v3.4 §11).
 *
 * Each chip names the record a scene cites; the quoted words are on hover.
 * A record outside the section being played is marked "from Hard Times" so
 * the coordinator sees when a scene strays. A scene the coordinator added is
 * "from your note". A quote that no longer matches its record — someone has
 * edited the record since — carries a "stale" badge. Stale never blocks
 * anything; it is there to be noticed.
 *
 * citations: [{path, quote, in_focus, status, label, section}] as the server
 * annotates them. origin: "coordinator" for a scene the coordinator added.
 */
export default function CitationChips({ citations, focusSection, origin }) {
  const list = (citations || []).filter(c => !(c.path || "").startsWith("plan.scenes["));
  const fromNote = origin === "coordinator" ||
    (citations || []).some(c => (c.path || "").startsWith("plan.scenes["));

  if (!list.length && !fromNote) {
    return <p style={st.none}>No source cited</p>;
  }
  return (
    <div style={st.row} aria-label="Drawn from">
      <span style={st.lead}>Drawn from</span>
      {fromNote && <span style={{ ...st.chip, ...st.note }}>from your note</span>}
      {list.map((c, i) => {
        const away = c.in_focus === false || (c.section && focusSection && c.section !== focusSection);
        const where = away && c.section ? SECTION_LABELS[c.section] || c.section : null;
        return (
          <span key={`${c.path}-${i}`} style={{ ...st.chip, ...(away ? st.away : {}) }}
                title={c.quote ? `“${c.quote}”` : undefined}>
            {c.label || c.path}
            {where && <span style={st.where}> · from {where}</span>}
            {c.status === "stale" && <span style={st.stale}>stale</span>}
            {c.status === "invalid" && <span style={st.stale}>not found</span>}
          </span>
        );
      })}
    </div>
  );
}

const st = {
  row:   { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-1)" },
  lead:  { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", marginRight: "var(--space-1)" },
  chip:  { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "var(--color-bg-inset)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", display: "inline-flex", alignItems: "center", gap: 4, cursor: "default" },
  away:  { background: "rgba(245,158,11,0.10)", color: "#92400e" },
  note:  { background: "var(--color-accent-subtle)", color: "var(--color-accent)" },
  where: { fontStyle: "italic" },
  stale: { fontSize: 9, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.05em", color: "#fff", background: "#d97706", borderRadius: "var(--radius-full)", padding: "1px 6px" },
  none:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: 0 },
};
